import { useState } from "react";
import { useNavigate } from "react-router";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CheckBox, PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { Layout } from "../UI/Layout/Layout.tsx";
import { NavBar } from "../UI/Layout/NavBar.tsx";
import { MainContainer } from "../UI/Layout/MainContainer.tsx";
import { Footer } from "../UI/Layout/Footer.tsx";
import { BillingFormSection } from "../NormasTeachingCheckout/BillingFormSection.tsx";
import { useAnalyticsParams } from "../MoneyTransfer/useAnalyticsParams.ts";
import { useCreateBonificoDraft } from "../ProductService/BonificoOrder/useCreateBonificoDraft.ts";
import {
  toBonificoBusinessInformation,
  toBonificoCustomerTaxCode,
} from "../ProductService/BonificoOrder/toBonificoBusinessInformation.ts";
import { ItalianBillingCountry } from "../MoneyTransfer/billingCountries.ts";
import {
  bonificoBillingSchemaFields,
  hasSdiOrPec,
} from "./bonificoBillingValidation.ts";
import {
  BonificoBankDetailsScreenPath,
  BonificoBankDetailsState,
} from "./BonificoBankDetailsScreen.tsx";

export const BonificoCheckoutScreenPath = "/bonifico";

const schema = yup
  .object({
    customer: yup.object({
      firstName: yup
        .string()
        .trim()
        .min(2, "Nome troppo corto")
        .required("Nome richiesto"),
      lastName: yup
        .string()
        .trim()
        .min(2, "Cognome troppo corto")
        .required("Cognome richiesto"),
      email: yup
        .string()
        .trim()
        .email("Email non valida")
        .required("Email richiesta"),
    }),
    ...bonificoBillingSchemaFields,
    privacyPolicyAccepted: yup
      .boolean()
      .oneOf([true], "Devi accettare la privacy policy")
      .required("Devi accettare la privacy policy"),
    additionalTerms: yup
      .boolean()
      .oneOf([true], "Devi accettare le clausole aggiuntive")
      .required("Devi accettare le clausole aggiuntive"),
  })
  .test("has-at-least-sdi-or-pec", "", hasSdiOrPec);

type FormValues = yup.InferType<typeof schema>;

export function BonificoCheckoutScreen() {
  const navigate = useNavigate();
  const { analyticsParams } = useAnalyticsParams();
  const { createDraft, loading } = useCreateBonificoDraft();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      customer: { firstName: "", lastName: "", email: "" },
      invoiceRequired: true,
      businessType: "",
      businessName: "",
      vat: "",
      taxCode: "",
      address: {
        street: "",
        cap: "",
        city: "",
        province: "",
        country: ItalianBillingCountry.Italy,
      },
      pec: "",
      sdiCode: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setSubmitError(null);
    const hasAnalytics =
      analyticsParams.utmSource ||
      analyticsParams.utmMedium ||
      analyticsParams.utmCampaign;
    try {
      await createDraft({
        customer: {
          firstName: data.customer.firstName.trim(),
          lastName: data.customer.lastName.trim(),
          email: data.customer.email.trim(),
          taxCode: toBonificoCustomerTaxCode(data),
        },
        businessInformation: toBonificoBusinessInformation(data),
        analytics: hasAnalytics
          ? {
              utmSource: analyticsParams.utmSource ?? "",
              utmMedium: analyticsParams.utmMedium ?? "",
              utmCampaign: analyticsParams.utmCampaign ?? "",
            }
          : undefined,
        privacyPolicyAccepted: true,
        additionalTermsAccepted: true,
      });
      const state: BonificoBankDetailsState = {
        firstName: data.customer.firstName,
        lastName: data.customer.lastName,
        taxCode: toBonificoCustomerTaxCode(data) ?? "",
        email: data.customer.email,
      };
      navigate(BonificoBankDetailsScreenPath, { state });
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      setSubmitError(
        status === 429
          ? "Hai effettuato troppi tentativi, riprova tra qualche minuto."
          : "Non è stato possibile inviare i tuoi dati. Riprova.",
      );
    }
  };

  return (
    <Layout>
      <NavBar />
      <MainContainer>
        <div className="max-w-2xl mx-auto p-6 bg-white w-full">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pagamento con bonifico
            </h1>
            <p className="text-gray-600">
              Compila i tuoi dati: dopo la conferma troverai le coordinate
              bancarie per effettuare il bonifico.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-md font-bold text-gray-900 mb-2">
                Nome
              </label>
              <TextInput
                {...register("customer.firstName")}
                errorMessage={errors.customer?.firstName?.message}
                placeholder="Il tuo nome"
              />
            </div>

            <div>
              <label className="block text-md font-bold text-gray-900 mb-2">
                Cognome
              </label>
              <TextInput
                {...register("customer.lastName")}
                errorMessage={errors.customer?.lastName?.message}
                placeholder="Il tuo cognome"
              />
            </div>

            <div>
              <label className="block text-md font-bold text-gray-900 mb-2">
                Email
              </label>
              <TextInput
                type="email"
                {...register("customer.email")}
                errorMessage={errors.customer?.email?.message}
                placeholder="La tua email"
              />
            </div>

            <BillingFormSection
              control={control}
              register={register}
              errors={errors}
              invoiceRequired={true}
              businessType={watch("businessType")}
              taxCode={watch("taxCode")}
              isBusinessInformationRequired={true}
              isInvoiceMandatory={true}
              trigger={trigger}
              askBillingCountry
            />

            {submitError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {submitError}
              </p>
            )}

            <div className="pt-6 flex flex-col gap-2">
              <PrimaryButton
                loading={loading}
                onClick={handleSubmit(onSubmit)}
                label={"Conferma e vai ai dati del bonifico"}
              />
              <div className={"flex justify-left"}>
                <Controller
                  render={({ field }) => (
                    <CheckBox
                      label={
                        <span>
                          <a
                            className={"text-blue-500 underline"}
                            target={"_blank"}
                            rel={"noopener noreferrer"}
                            href="https://normasteaching.com/legal-info"
                          >
                            Accetto la privacy policy e i termini e condizioni
                          </a>
                        </span>
                      }
                      value={field.value}
                      onChange={field.onChange}
                      errorMessage={errors.privacyPolicyAccepted?.message}
                    />
                  )}
                  name={"privacyPolicyAccepted"}
                  control={control}
                />
              </div>
              <div className={"flex justify-left"}>
                <Controller
                  render={({ field }) => (
                    <CheckBox
                      label={
                        <span>
                          Ai sensi degli artt. 1341 e 1342 c.c., dichiaro di
                          accettare espressamente le clausole delle condizioni
                          generali di vendita: artt. 2.1, 4.3, 6, 10, 13, 16.2,
                          18.5, 20, 21.5, 21.6, 23.2, 26.7, 26.14, 29, 31, 39,
                          41, 47 e 48.
                        </span>
                      }
                      value={field.value}
                      onChange={field.onChange}
                      errorMessage={errors.additionalTerms?.message}
                    />
                  )}
                  name={"additionalTerms"}
                  control={control}
                />
              </div>
            </div>
          </div>
        </div>
      </MainContainer>
      <Footer />
    </Layout>
  );
}
